// src/components/artwork/tabs/ArtworkLifecycleTab.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  MapPin,
  Shield,
  AlertTriangle,
  TrendingUp,
  Wrench,
  Stethoscope,
  ClipboardCheck,
  Truck,
  ShoppingBag,
  FolderPlus,
  Handshake,
  Landmark,
  FileSignature,
  Tag,
  Gift,
  LogOut,
  FileText,
  StickyNote,
  Clock,
  Plus,
  X,
} from "lucide-react";
import { supabase } from "../../../lib/supabaseClient";
import { useTranslation } from "react-i18next";

// Types qu'un utilisateur peut déclarer à la main — les types purement
// automatiques (acquisition, valorisation, localisation, assurance, sinistre,
// propriété) restent réservés aux triggers pour ne pas mélanger données
// système et données déclarées. Doit rester synchronisé avec la whitelist
// côté RPC add_artwork_lifecycle_event.
const MANUAL_EVENT_TYPES = [
  "note",
  "loan",
  "exhibition",
  "collection_entry",
  "sale_mandate",
  "sale",
  "donation",
  "deaccession",
  "document_added",
  "transport",
  "condition_check",
  "restoration",
  "conservation_action",
];

const EVENT_META = {
  acquisition: { icon: ShoppingBag, label: "Acquisition", color: "#166534" },
  collection_entry: { icon: FolderPlus, label: "Entrée dans la collection", color: "#166534" },
  location_change: { icon: MapPin, label: "Changement de localisation", color: "#1d4ed8" },
  transport: { icon: Truck, label: "Transport", color: "#1d4ed8" },
  condition_check: { icon: ClipboardCheck, label: "Constat d'état", color: "#0891b2" },
  loan: { icon: Handshake, label: "Prêt", color: "#7c3aed" },
  exhibition: { icon: Landmark, label: "Exposition", color: "#7c3aed" },
  conservation_action: { icon: Stethoscope, label: "Conservation préventive", color: "#0891b2" },
  restoration: { icon: Wrench, label: "Restauration", color: "#0891b2" },
  insurance_update: { icon: Shield, label: "Assurance", color: "#a16207" },
  claim: { icon: AlertTriangle, label: "Sinistre", color: "#b91c1c" },
  valuation: { icon: TrendingUp, label: "Valorisation", color: "#a16207" },
  ownership_change: { icon: FileSignature, label: "Changement de propriété", color: "#374151" },
  sale_mandate: { icon: Tag, label: "Mise en vente", color: "#374151" },
  sale: { icon: Tag, label: "Vente", color: "#374151" },
  donation: { icon: Gift, label: "Don", color: "#374151" },
  deaccession: { icon: LogOut, label: "Sortie de collection", color: "#374151" },
  document_added: { icon: FileText, label: "Document ajouté", color: "#6b7280" },
  note: { icon: StickyNote, label: "Note", color: "#6b7280" },
};

function metaFor(eventType) {
  return EVENT_META[eventType] || { icon: Clock, label: eventType || "Événement", color: "#6b7280" };
}

function metadataHighlights(ev) {
  const m = ev.metadata || {};
  const parts = [];
  switch (ev.event_type) {
    case "insurance_update":
      if (m.insurer) parts.push(m.insurer);
      if (m.value != null) parts.push(`${Number(m.value).toLocaleString("fr-FR")} €`);
      break;
    case "claim":
      if (m.loss_type) parts.push(m.loss_type);
      if (m.declared_value != null) parts.push(`${Number(m.declared_value).toLocaleString("fr-FR")} €`);
      if (m.recovered) parts.push("récupérée");
      break;
    case "valuation":
      if (m.amount_retained != null) parts.push(`${Number(m.amount_retained).toLocaleString("fr-FR")} ${m.currency || "€"}`);
      else if (m.amount_min != null || m.amount_max != null) parts.push(`${m.amount_min ?? "?"} - ${m.amount_max ?? "?"} ${m.currency || "€"}`);
      if (m.method) parts.push(m.method);
      break;
    case "restoration":
    case "conservation_action":
      if (m.type) parts.push(m.type);
      if (m.contact) parts.push(m.contact);
      if (m.cost != null) parts.push(`${Number(m.cost).toLocaleString("fr-FR")} €`);
      break;
    case "condition_check":
      if (m.status) parts.push(m.status);
      if (m.risk_level) parts.push(`risque: ${m.risk_level}`);
      break;
    case "transport":
      if (m.trip_type) parts.push(m.trip_type);
      if (m.status) parts.push(m.status);
      break;
    case "location_change":
      if (m.zone) parts.push(m.zone);
      if (m.status) parts.push(m.status);
      break;
    default:
      break;
  }
  return parts;
}

function formatDate(value, locale) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleDateString(locale || "fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function AddEventModal({ artworkId, onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    event_type: "note",
    title: "",
    description: "",
    event_date_start: new Date().toISOString().slice(0, 10),
    event_date_end: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setErr(t("artworkDetail.lifecycle.form.titleRequired", { defaultValue: "Le titre est obligatoire" }));
      return;
    }
    setSaving(true);
    setErr(null);
    const { error } = await supabase.rpc("add_artwork_lifecycle_event", {
      p_artwork_id: artworkId,
      p_event_type: form.event_type,
      p_title: form.title.trim(),
      p_description: form.description.trim() || null,
      p_event_date_start: form.event_date_start || null,
      p_event_date_end: form.event_date_end || null,
    });
    setSaving(false);
    if (error) {
      setErr(error.message || t("artworkDetail.lifecycle.form.error", { defaultValue: "Impossible d'enregistrer l'événement" }));
      return;
    }
    onSaved?.();
    onClose();
  };

  const inputCls = "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-gray-400";
  const labelCls = "block text-xs font-medium text-gray-500 mb-1";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-serif text-lg" style={{ fontFamily: "Georgia, serif" }}>
            {t("artworkDetail.lifecycle.form.title", { defaultValue: "Ajouter un événement" })}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-black">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={labelCls}>{t("artworkDetail.lifecycle.form.type", { defaultValue: "Type d'événement" })}</label>
            <select className={inputCls} value={form.event_type} onChange={(e) => set("event_type", e.target.value)}>
              {MANUAL_EVENT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {metaFor(type).label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelCls}>{t("artworkDetail.lifecycle.form.titleLabel", { defaultValue: "Titre" })}</label>
            <input
              type="text"
              className={inputCls}
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder={t("artworkDetail.lifecycle.form.titlePlaceholder", { defaultValue: "Ex. : Prêt au musée X" })}
              required
            />
          </div>

          <div>
            <label className={labelCls}>{t("artworkDetail.lifecycle.form.description", { defaultValue: "Description (optionnel)" })}</label>
            <textarea
              className={inputCls}
              rows={3}
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>{t("artworkDetail.lifecycle.form.dateStart", { defaultValue: "Date" })}</label>
              <input
                type="date"
                className={inputCls}
                value={form.event_date_start}
                onChange={(e) => set("event_date_start", e.target.value)}
              />
            </div>
            <div>
              <label className={labelCls}>{t("artworkDetail.lifecycle.form.dateEnd", { defaultValue: "Date de fin (optionnel)" })}</label>
              <input
                type="date"
                className={inputCls}
                value={form.event_date_end}
                onChange={(e) => set("event_date_end", e.target.value)}
              />
            </div>
          </div>

          {err && <p className="text-xs text-red-600">{err}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-black">
              {t("artworkDetail.lifecycle.form.cancel", { defaultValue: "Annuler" })}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-900 disabled:opacity-50"
            >
              {saving
                ? t("artworkDetail.lifecycle.form.saving", { defaultValue: "Enregistrement…" })
                : t("artworkDetail.lifecycle.form.save", { defaultValue: "Enregistrer" })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ArtworkLifecycleTab({ artworkId, locale, canEdit = false }) {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);

  const load = React.useCallback(async () => {
    if (!artworkId) return;
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from("artwork_lifecycle_events")
      .select("*")
      .eq("artwork_id", artworkId)
      .is("deleted_at", null)
      .order("event_date_start", { ascending: false });

    if (err) {
      setError(err.message || "Erreur de chargement");
      setEvents([]);
    } else {
      setEvents(data || []);
    }
    setLoading(false);
  }, [artworkId]);

  useEffect(() => {
    load();
  }, [load]);

  const addButton = canEdit && (
    <button
      type="button"
      onClick={() => setShowAddModal(true)}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-black text-white hover:bg-gray-900 transition"
    >
      <Plus className="w-3.5 h-3.5" />
      {t("artworkDetail.lifecycle.addEvent", { defaultValue: "Ajouter un événement" })}
    </button>
  );

  const modal = showAddModal && (
    <AddEventModal artworkId={artworkId} onClose={() => setShowAddModal(false)} onSaved={load} />
  );

  const types = useMemo(() => {
    const set = new Set(events.map((e) => e.event_type));
    return Array.from(set);
  }, [events]);

  const filtered = useMemo(() => {
    if (filter === "all") return events;
    return events.filter((e) => e.event_type === filter);
  }, [events, filter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900 mr-3" />
        {t("artworkDetail.lifecycle.loading", { defaultValue: "Chargement de la chronologie…" })}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-red-600 text-sm">
        {t("artworkDetail.lifecycle.error", { defaultValue: "Impossible de charger la chronologie." })}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
          <Clock className="w-8 h-8 text-gray-400" />
        </div>
        <p className="font-medium mb-1">
          {t("artworkDetail.lifecycle.empty", { defaultValue: "Aucun événement enregistré pour cette œuvre" })}
        </p>
        <p className="text-xs text-gray-400 max-w-sm mx-auto mb-4">
          {t("artworkDetail.lifecycle.emptyDesc", {
            defaultValue:
              "Les mouvements, constats, assurances, restaurations et valorisations apparaîtront ici automatiquement au fil de la gestion de l'œuvre.",
          })}
        </p>
        {canEdit && <div className="flex justify-center">{addButton}</div>}
        {modal}
      </div>
    );
  }

  return (
    <div className="text-sm">
      <div className="mb-6 flex items-center justify-between gap-3">
        {types.length > 1 ? (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilter("all")}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                filter === "all" ? "bg-black text-white border-black" : "border-gray-200 text-gray-600 hover:border-gray-400"
              }`}
            >
              {t("artworkDetail.lifecycle.all", { defaultValue: "Tout" })} ({events.length})
            </button>
            {types.map((type) => {
              const meta = metaFor(type);
              const count = events.filter((e) => e.event_type === type).length;
              return (
                <button
                  key={type}
                  onClick={() => setFilter(type)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                    filter === type ? "text-white border-transparent" : "border-gray-200 text-gray-600 hover:border-gray-400"
                  }`}
                  style={filter === type ? { backgroundColor: meta.color } : undefined}
                >
                  {meta.label} ({count})
                </button>
              );
            })}
          </div>
        ) : (
          <div />
        )}
        {addButton}
      </div>
      {modal}

      <ol className="relative border-l border-gray-200 ml-3">
        {filtered.map((ev) => {
          const meta = metaFor(ev.event_type);
          const Icon = meta.icon;
          const highlights = metadataHighlights(ev);

          return (
            <li key={ev.id} className="mb-6 ml-6">
              <span
                className="absolute flex items-center justify-center w-7 h-7 rounded-full -left-3.5 ring-4 ring-white"
                style={{ backgroundColor: `${meta.color}1a` }}
              >
                <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
              </span>
              <div className="rounded-lg border border-gray-200 p-4 hover:border-gray-300 transition">
                <div className="flex items-center justify-between gap-3 mb-1">
                  <span className="font-medium text-gray-900 flex items-center gap-1.5">
                    {ev.title || meta.label}
                    {ev.metadata?.manual && (
                      <span className="text-[10px] font-normal uppercase tracking-wide text-gray-400 border border-gray-200 rounded-full px-1.5 py-0.5">
                        {t("artworkDetail.lifecycle.manualBadge", { defaultValue: "déclaré" })}
                      </span>
                    )}
                  </span>
                  <time className="text-xs text-gray-400 whitespace-nowrap">
                    {formatDate(ev.event_date_start, locale)}
                    {ev.event_date_end ? ` → ${formatDate(ev.event_date_end, locale)}` : ""}
                  </time>
                </div>
                {ev.description && <p className="text-gray-600 text-xs mb-1">{ev.description}</p>}
                {highlights.length > 0 && (
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5">
                    {highlights.map((h, i) => (
                      <span key={i} className="text-xs text-gray-500">
                        {h}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
